/**
 * Hook: useMultiSourceWaitTime
 * Wraps the multi-source wait time service and adapts to existing UI shape.
 * Non-breaking: the existing useAirportWaitTime hook is NOT modified.
 * This hook can be used as a drop-in replacement or alongside it.
 */
import { useState, useEffect } from 'react';
import { getWaitTime, WaitTimeResult, WaitTimeSource } from '@/services/waitTimes';

interface StandardWaitTime {
  waitTime: number;
  status: string;
  statusColor: string;
  description: string;
}

interface PrecheckWaitTime {
  waitTime?: number;
  status?: string;
  statusColor?: string;
  description?: string;
  available: boolean;
}

/** Matches the existing WaitTimeData shape from useAirportWaitTime */
interface WaitTimeData {
  standard: StandardWaitTime;
  precheck: PrecheckWaitTime;
  lastUpdated: string;
  error?: boolean;
  /** New optional fields */
  source?: WaitTimeSource;
  confidence?: number;
  disclaimer?: string;
}

const getStatusInfo = (waitTime: number) => {
  if (waitTime <= 5) return { status: 'Low', statusColor: 'green' };
  if (waitTime <= 15) return { status: 'Medium', statusColor: 'yellow' };
  return { status: 'High', statusColor: 'red' };
};

export const useMultiSourceWaitTime = (airportCode: string | undefined) => {
  const [waitTime, setWaitTime] = useState<WaitTimeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!airportCode) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getWaitTime(airportCode);

        if (cancelled) return;

        if (!result || result.waitTimeMinutes === null) {
          setWaitTime(null);
          setError('Wait time data unavailable');
          return;
        }

        const standardStatus = getStatusInfo(result.waitTimeMinutes);
        // Estimate PreCheck as ~40% of standard
        const precheckTime = Math.max(1, Math.round(result.waitTimeMinutes * 0.4));
        const precheckStatus = getStatusInfo(precheckTime);

        setWaitTime({
          standard: {
            waitTime: result.waitTimeMinutes,
            status: standardStatus.status,
            statusColor: standardStatus.statusColor,
            description: `${result.waitTimeMinutes} minutes`,
          },
          precheck: {
            waitTime: precheckTime,
            status: precheckStatus.status,
            statusColor: precheckStatus.statusColor,
            description: `${precheckTime} minutes`,
            available: true,
          },
          lastUpdated: result.lastUpdatedISO,
          source: result.source,
          confidence: result.confidence,
          disclaimer: result.disclaimer,
        });
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error in multi-source wait time:', err);
        setError('Unable to load wait time');
        setWaitTime(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => { cancelled = true; };
  }, [airportCode]);

  return { waitTime, loading, error };
};
